import { useEffect, useState } from "react";
import type { SystemStatus } from "@/api/types";

interface StatusBadgesProps {
  systemStatus: SystemStatus | null;
}

type Tone = "ok" | "warn" | "bad";

function toneClasses(tone: Tone): string {
  if (tone === "bad") return "border-sell/50 bg-sell/10 text-sell";
  if (tone === "warn") return "border-gold/50 bg-gold/10 text-gold";
  return "border-turquoise/50 bg-turquoise/10 text-turquoise";
}

function Dot({ tone }: { tone: Tone }) {
  const color = tone === "bad" ? "bg-sell" : tone === "warn" ? "bg-gold" : "bg-turquoise";
  return (
    <span className="relative flex h-1.5 w-1.5">
      {tone !== "bad" && (
        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${color} opacity-60`} />
      )}
      <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${color}`} />
    </span>
  );
}

function Badge({ label, tone, title }: { label: string; tone: Tone; title?: string }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-wider ${toneClasses(tone)}`}
    >
      <Dot tone={tone} />
      {label}
    </span>
  );
}

// A tick is expected within ~90s of every 10-minute mark (cron, or the Durable Object
// heartbeat as backup). Being overdue by more than this means neither one ran.
const STALL_AFTER_MS = 5 * 60_000;

function formatCountdown(targetMs: number): string {
  const diffSeconds = Math.round((targetMs - Date.now()) / 1000);
  if (diffSeconds <= 0) return "due now";
  if (diffSeconds < 60) return `in ${diffSeconds}s`;
  return `in ${Math.round(diffSeconds / 60)}m`;
}

function formatAgo(timestampMs: number): string {
  const minutes = Math.max(1, Math.round((Date.now() - timestampMs) / 60_000));
  if (minutes < 120) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

function describeScheduler(scheduler: SystemStatus["scheduler"]): { label: string; tone: Tone } {
  if (scheduler.isRunning) {
    return { label: `Scheduler · Running (${scheduler.triggerSource ?? "unknown"})`, tone: "warn" };
  }
  const next = scheduler.nextScheduledAt;
  if (next === null) {
    return { label: "Scheduler · Waiting for first run", tone: "warn" };
  }
  if (Date.now() - next > STALL_AFTER_MS) {
    const last = scheduler.lastScheduledCycleAt;
    return {
      label: last !== null ? `Scheduler · Stalled (last run ${formatAgo(last)})` : "Scheduler · Stalled",
      tone: "bad",
    };
  }
  return { label: `Scheduler · Next ${formatCountdown(next)}`, tone: "ok" };
}

export default function StatusBadges({ systemStatus }: StatusBadgesProps) {
  const [, forceTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!systemStatus) return null;
  const { exchange, ai, scheduler } = systemStatus;

  const exchangeTone: Tone = exchange.status === "error" ? "bad" : "ok";
  const exchangeLabel = exchange.status === "paper" ? "Exchange · Paper" : exchange.status === "error" ? "Exchange · Error" : "Exchange · Connected";

  const aiTone: Tone = ai.status === "error" ? "bad" : ai.status === "unknown" ? "warn" : "ok";
  const aiLabel = `AI · ${ai.status === "error" ? "Error" : ai.status === "unknown" ? "No Data" : "OK"}`;

  const { label: schedulerLabel, tone: schedulerTone } = describeScheduler(scheduler);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge label={exchangeLabel} tone={exchangeTone} title={exchange.message ?? undefined} />
      <Badge
        label={aiLabel}
        tone={aiTone}
        title={ai.message ?? (ai.lastDecisionAt ? `Last decision ${new Date(ai.lastDecisionAt).toLocaleTimeString()}` : undefined)}
      />
      <Badge
        label={schedulerLabel}
        tone={schedulerTone}
        title={`Scan interval ${scheduler.scanIntervalMinutes}m · Cron ${scheduler.cronIntervalMinutes}m`}
      />
    </div>
  );
}
